import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus, Search, ArrowLeft, Music2, UploadCloud, Sparkles, FileText, Youtube, ExternalLink,
  Tv, Pencil, Check, Layers, Trash2,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, KeyBadge, BpmBadge, MetaBadge, Field, inputCls, StatusBadge } from "@/components/ui-kit";
import ChordViewer from "@/components/ChordViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KEYS } from "@/lib/transpose";
import { toast } from "sonner";

const TAGS = ["Worship", "Praise", "Prayer", "Communion", "Offering", "Response", "Opening", "Closing", "Slow", "Fast", "Acoustic", "Full Band", "Christmas", "Easter", "Youth", "Special Event"];

function SongForm({ initial, onSave }) {
  const [f, setF] = useState(initial);
  const toggleTag = (t) => setF({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title"><input className={inputCls} data-testid="song-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Artist / source"><input className={inputCls} value={f.artist} onChange={(e) => setF({ ...f, artist: e.target.value })} /></Field>
        <Field label="Writer"><input className={inputCls} value={f.writer} onChange={(e) => setF({ ...f, writer: e.target.value })} /></Field>
        <Field label="CCLI #"><input className={inputCls} value={f.ccli} onChange={(e) => setF({ ...f, ccli: e.target.value })} /></Field>
        <Field label="Original key"><select className={inputCls} value={f.original_key} onChange={(e) => setF({ ...f, original_key: e.target.value })}>{KEYS.map((k) => <option key={k}>{k}</option>)}</select></Field>
        <Field label="Arrangement key"><select className={inputCls} value={f.arrangement_key} onChange={(e) => setF({ ...f, arrangement_key: e.target.value })}>{KEYS.map((k) => <option key={k}>{k}</option>)}</select></Field>
        <Field label="BPM"><input type="number" className={inputCls} value={f.bpm} onChange={(e) => setF({ ...f, bpm: Number(e.target.value) })} /></Field>
        <Field label="Time signature"><input className={inputCls} value={f.time_signature} onChange={(e) => setF({ ...f, time_signature: e.target.value })} /></Field>
        <Field label="Duration"><input className={inputCls} value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} /></Field>
        <Field label="YouTube link"><input className={inputCls} value={f.links?.youtube || ""} onChange={(e) => setF({ ...f, links: { ...f.links, youtube: e.target.value } })} /></Field>
      </div>
      <Field label="Tags">
        <div className="flex flex-wrap gap-1.5">
          {TAGS.map((t) => <button key={t} type="button" onClick={() => toggleTag(t)} className={`px-2.5 py-1 rounded-lg text-xs border ${f.tags.includes(t) ? "bg-[#E5C378]/15 text-[#E5C378] border-[#E5C378]/30" : "bg-white/5 text-[#8A97AC] border-white/10"}`}>{t}</button>)}
        </div>
      </Field>
      <Field label="Lyrics"><textarea className={inputCls} rows={4} value={f.lyrics} onChange={(e) => setF({ ...f, lyrics: e.target.value })} /></Field>
      <button onClick={() => onSave(f)} data-testid="save-song-button" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">Save song</button>
    </div>
  );
}

const EMPTY_SONG = { title: "", artist: "", writer: "", ccli: "", original_key: "C", arrangement_key: "C", bpm: 72, time_signature: "4/4", duration: "", genre: "Worship", language: "English", tags: [], lyrics: "", chords: [], sections: [], links: {}, notes: "" };

export function Songs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [songs, setSongs] = useState([]);
  const [q, setQ] = useState("");
  const [key, setKey] = useState("");
  const [tag, setTag] = useState("");
  const [open, setOpen] = useState(false);
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q); if (key) params.set("key", key); if (tag) params.set("tag", tag);
    const { data } = await api.get(`/songs?${params}`); setSongs(data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, key, tag]);

  const create = async (f) => {
    try { const { data } = await api.post("/songs", f); toast.success("Song added"); setOpen(false); nav(`/songs/${data.id}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="The repertoire" title="Song Library"
        subtitle="Every song your team knows — searchable by title, key, tempo and tag."
        actions={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="btn-add-new-song" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm"><Plus className="w-4 h-4" /> Add song</button></DialogTrigger>
            <DialogContent className="wf-glass border-white/10 max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">New song</DialogTitle></DialogHeader>
              <SongForm initial={EMPTY_SONG} onSave={create} />
            </DialogContent>
          </Dialog>
        )} />

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E7D97]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="song-search" placeholder="Search songs, artists, writers…" className={`${inputCls} pl-9`} />
        </div>
        <select value={key} onChange={(e) => setKey(e.target.value)} data-testid="song-filter-key" className="bg-[#0B101D] border border-white/10 rounded-xl px-3 text-sm text-[#F7F5F0]"><option value="">Any key</option>{KEYS.map((k) => <option key={k}>{k}</option>)}</select>
        <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="song-filter-tag" className="bg-[#0B101D] border border-white/10 rounded-xl px-3 text-sm text-[#F7F5F0]"><option value="">Any tag</option>{TAGS.map((t) => <option key={t}>{t}</option>)}</select>
      </div>

      {songs.length === 0 ? (
        <EmptyState icon={Music2} title="No songs yet" body="Add your first song or upload a chart to begin building the library." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {songs.map((s) => (
            <button key={s.id} data-testid={`song-card-${s.id}`} onClick={() => nav(`/songs/${s.id}`)}
              className="wf-card rounded-xl p-5 text-left group transition-all hover:-translate-y-0.5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-[#F7F5F0] group-hover:text-[#E5C378] transition-colors truncate">{s.title}</div>
                  <div className="text-xs text-[#8A97AC] uppercase tracking-wider font-mono truncate">{s.artist}</div>
                </div>
                {s.charts?.length > 0 && <FileText className="w-4 h-4 text-[#7EA293] shrink-0" />}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <KeyBadge>{s.arrangement_key}</KeyBadge>
                <BpmBadge>{s.bpm} BPM</BpmBadge>
                <MetaBadge>{s.time_signature}</MetaBadge>
              </div>
              {s.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-3">{s.tags.slice(0, 3).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-[#8A97AC]">{t}</span>)}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartUpload({ song, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [detected, setDetected] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true); setDetected(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDetected(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not analyze this file."); }
    finally { setAnalyzing(false); }
  };

  const save = async ({ applyMeta }) => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (detected) { fd.append("extracted", JSON.stringify(detected)); fd.append("confidence", detected.confidence || 0); fd.append("needs_review", detected.needs_review || false); }
      await api.post(`/songs/${song.id}/charts`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (applyMeta && detected) {
        const upd = { ...song };
        if (detected.key) upd.arrangement_key = detected.key.replace(/\s/g, "");
        if (detected.bpm) upd.bpm = detected.bpm;
        if (detected.time_signature) upd.time_signature = detected.time_signature;
        if (detected.chords) upd.chords = detected.chords;
        delete upd.charts; delete upd.arrangements;
        await api.put(`/songs/${song.id}`, upd);
      }
      toast.success("Chart saved");
      setOpen(false); setFile(null); setDetected(null); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="upload-chart-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] text-sm hover:border-[#E5C378]/30"><UploadCloud className="w-4 h-4" /> Upload chart</button>
      </DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Upload music sheet</DialogTitle></DialogHeader>
        <div className="text-xs text-[#6E7D97] mb-1">PDF, PNG or JPG. Only upload material the church is legally permitted to use.</div>
        <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { setFile(e.target.files[0]); setDetected(null); }} data-testid="chart-file-input"
          className="block w-full text-sm text-[#B8C1D1] file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E5C378] file:text-[#0A0E1A] file:font-semibold" />
        {file && !detected && (
          <button onClick={analyze} disabled={analyzing} data-testid="analyze-chart-button" className="w-full py-2.5 rounded-xl bg-[#4B7BE5]/20 border border-[#4B7BE5]/40 text-[#8FB0FF] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            <Sparkles className="w-4 h-4" /> {analyzing ? "Analyzing chart…" : "Analyze with AI"}
          </button>
        )}
        {detected && (
          <div className="bg-[#0B101D] border border-[#E5C378]/25 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> AI detected information</div>
              {detected.needs_review ? <StatusBadge status="preparing" /> : <span className="text-xs text-emerald-300">Confidence {Math.round((detected.confidence || 0) * 100)}%</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[["Title", detected.title], ["Key", detected.key], ["BPM", detected.bpm], ["Time sig", detected.time_signature], ["Capo", detected.capo]].map(([k, v]) => v ? (
                <div key={k} className="flex justify-between border-b border-white/5 py-1"><span className="text-[#8A97AC]">{k}</span><span className="text-[#F7F5F0] font-mono">{v}</span></div>
              ) : null)}
            </div>
            {detected.chords?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{detected.chords.map((c) => <KeyBadge key={c}>{c}</KeyBadge>)}</div>}
            {detected.needs_review && <p className="text-xs text-amber-300 mt-3">Low confidence — please review before applying to the song.</p>}
            <p className="text-[11px] text-[#6E7D97] mt-3">Review before saving. AI never overwrites approved song info unless you choose to apply it.</p>
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={() => save({ applyMeta: false })} disabled={!file || saving} data-testid="save-chart-only" className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] font-semibold text-sm disabled:opacity-50">Save chart only</button>
          {detected && <button onClick={() => save({ applyMeta: true })} disabled={saving} data-testid="save-chart-apply" className="flex-1 py-2.5 rounded-xl wf-gold-btn font-semibold text-sm flex items-center justify-center gap-1.5"><Check className="w-4 h-4" /> Save & apply</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SongDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [song, setSong] = useState(null);
  const [editing, setEditing] = useState(false);
  const [arr, setArr] = useState({ name: "", key: "C", bpm: 72, notes: "" });
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);

  const load = () => api.get(`/songs/${id}`).then((r) => setSong(r.data));
  useEffect(() => { load(); }, [id]);
  if (!song) return <div className="text-[#8A97AC]">Loading…</div>;

  const saveEdit = async (f) => {
    try { const upd = { ...f }; delete upd.charts; delete upd.arrangements; await api.put(`/songs/${id}`, upd); toast.success("Saved"); setEditing(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addArr = async () => {
    if (!arr.name) return;
    await api.post(`/songs/${id}/arrangements`, arr); setArr({ name: "", key: "C", bpm: 72, notes: "" }); load(); toast.success("Arrangement added");
  };

  return (
    <div>
      <button onClick={() => nav("/songs")} className="flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378] mb-4"><ArrowLeft className="w-4 h-4" /> Library</button>
      <div className="wf-card rounded-2xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#F7F5F0]">{song.title}</h1>
            <div className="text-[#E5C378] mt-0.5">{song.artist}</div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <KeyBadge>{song.arrangement_key}</KeyBadge><BpmBadge>{song.bpm} BPM</BpmBadge><MetaBadge>{song.time_signature}</MetaBadge>
              {song.duration && <MetaBadge>{song.duration}</MetaBadge>}
              {song.ccli && <MetaBadge>CCLI {song.ccli}</MetaBadge>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">{song.tags?.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-[#8A97AC]">{t}</span>)}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => nav(`/stage/${song.id}`)} data-testid="open-stage-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#080C14] border border-[#E5C378]/30 text-[#E5C378] font-semibold text-sm"><Tv className="w-4 h-4" /> Stage</button>
            {canManage && <ChartUpload song={song} onDone={load} />}
            {canManage && (
              <Dialog open={editing} onOpenChange={setEditing}>
                <DialogTrigger asChild><button data-testid="edit-song-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] text-sm"><Pencil className="w-4 h-4" /> Edit</button></DialogTrigger>
                <DialogContent className="wf-glass border-white/10 max-w-2xl max-h-[90vh] overflow-auto">
                  <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Edit song</DialogTitle></DialogHeader>
                  <SongForm initial={{ ...EMPTY_SONG, ...song }} onSave={saveEdit} />
                </DialogContent>
              </Dialog>
            )}
            {canManage && (
              <button onClick={async () => { if (window.confirm(`Delete "${song.title}"? This can't be undone.`)) { try { await api.delete(`/songs/${song.id}`); toast.success("Song deleted"); nav("/songs"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } } }}
                data-testid="delete-song-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
            )}
          </div>
        </div>
        {(song.links?.youtube || song.links?.spotify) && (
          <div className="flex gap-3 mt-4">
            {song.links.youtube && <a href={song.links.youtube} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378]"><Youtube className="w-4 h-4" /> YouTube</a>}
            {song.links.spotify && <a href={song.links.spotify} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378]"><ExternalLink className="w-4 h-4" /> Spotify</a>}
          </div>
        )}
      </div>

      <Tabs defaultValue="chart">
        <TabsList className="bg-[#0B101D] border border-white/10 mb-5 flex-wrap h-auto">
          <TabsTrigger value="chart" data-testid="song-tab-chart">Chord Chart</TabsTrigger>
          <TabsTrigger value="charts" data-testid="song-tab-files">Uploaded Charts</TabsTrigger>
          <TabsTrigger value="arrangements" data-testid="song-tab-arrangements">Arrangements</TabsTrigger>
        </TabsList>

        <TabsContent value="chart">
          {song.sections?.length || song.lyrics ? <ChordViewer song={song} /> :
            <EmptyState icon={FileText} title="No chart yet" body="Upload a chart or add lyrics/chords by editing this song." />}
        </TabsContent>

        <TabsContent value="charts">
          {song.charts?.length === 0 ? (
            <EmptyState icon={UploadCloud} title="No files uploaded" body="Upload a PDF or image chart. We'll try to detect the key, BPM and chords for you." />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {song.charts.map((c) => (
                <a key={c.id} href={fileUrl(`worship-flow/charts/${song.id}/${c.file_id}.${c.ext}`)} target="_blank" rel="noreferrer" data-testid={`chart-file-${c.id}`}
                  className="wf-card rounded-xl p-4 flex items-center gap-3 hover:border-[#E5C378]/30">
                  <FileText className="w-8 h-8 text-[#E5C378]" />
                  <div className="min-w-0"><div className="text-sm text-[#F7F5F0] truncate">{c.filename}</div>
                    <div className="text-xs text-[#6E7D97]">Uploaded by {c.uploaded_by} {c.needs_review && <span className="text-amber-300">· Needs review</span>}</div></div>
                </a>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="arrangements">
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            {song.arrangements?.map((a) => (
              <div key={a.id} className="wf-card rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2"><Layers className="w-4 h-4 text-[#7EA293]" /><span className="font-medium text-[#F7F5F0]">{a.name}</span></div>
                <div className="flex gap-2"><KeyBadge>{a.key}</KeyBadge><BpmBadge>{a.bpm} BPM</BpmBadge></div>
                {a.notes && <p className="text-xs text-[#8A97AC] mt-2">{a.notes}</p>}
              </div>
            ))}
          </div>
          {canManage && (
            <div className="wf-card rounded-xl p-4 flex flex-wrap items-end gap-3">
              <Field label="Name"><input className={inputCls} value={arr.name} onChange={(e) => setArr({ ...arr, name: e.target.value })} placeholder="Acoustic / Sunday 27 Sep" /></Field>
              <Field label="Key"><select className={inputCls} value={arr.key} onChange={(e) => setArr({ ...arr, key: e.target.value })}>{KEYS.map((k) => <option key={k}>{k}</option>)}</select></Field>
              <Field label="BPM"><input type="number" className={`${inputCls} w-24`} value={arr.bpm} onChange={(e) => setArr({ ...arr, bpm: Number(e.target.value) })} /></Field>
              <button onClick={addArr} data-testid="add-arrangement-button" className="px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm">Add</button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
